import { useMemo, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useCurrentLocale } from '@locales/client';
import { useBonuses } from '@/features/profile/model/hooks';
import { useCashback } from '@/flat-pages/cashback/model/hooks';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { BANNERS_DATA, SORT_CONFIG_BANNERS } from './const';
import { useLoyaltyList } from '@/flat-pages/status/model/hooks';
import { CROWN_IMAGE_URL } from '@/flat-pages/status/ui/status-card';
import { useWheels } from '@/flat-pages/fortune-wheel/hooks';
import { useBrandTheme } from '@/shared/hooks';

interface BannerData {
  id: number;
  tKey: string;
  image: string;
  title: string;
  param: string;
  counter?: number;
  device?: 'desktop' | 'mobile' | 'both';
  auth?: 'all' | 'authenticated' | 'unauthenticated';
  disabled?: boolean;
  srcActive?: boolean;
}

interface UseBannersResult {
  sortedBanners: BannerData[];
  sortedMobileMenuBanners: BannerData[];
  sortedMobileBanners: BannerData[];
  linkBanners: (option: string) => string;
  linkMobileBanners: (option: Omit<BannerData, 'counter'>) => string;
  loading: boolean;
  categoryParam: string;
}

interface UseBannersProps {
  mobileLandscape: boolean;
}

const useBanners = ({ mobileLandscape }: UseBannersProps): UseBannersResult => {
  const { data: dataBonuses, isLoading: isLoadingBonuses } = useBonuses();
  const { data: dataCashback, isLoading: isLoadingCashback } = useCashback();
  const { totalCount: wheelsCount, isLoading: isLoadingWheels } = useWheels();
  const { loyaltyList } = useLoyaltyList();
  const { user } = useUser();
  const pathname = usePathname();
  const locale = useCurrentLocale();
  const { isMillion } = useBrandTheme();

  const categoryParam = useMemo(
    () => pathname.match(/\/games\/([^\/]+)/)?.[1] || '',
    [pathname]
  );

  const loading = isLoadingBonuses || isLoadingCashback || isLoadingWheels;

  const linkBanners = (option: string): string => {
    const gamesIndex = pathname.indexOf('games');
    const basePath = pathname.substring(0, gamesIndex + 5);

    if (gamesIndex === -1 && option === 'favorite') {
      option = `games/favorite`;
    }

    if (option === 'https://t.me/pingwincasino') {
      return isMillion ?'https://t.me/lotomillion_channel':option;
    }

    if (gamesIndex !== -1 && option === 'betting') {
      return `/${locale}/betting`;
    }

    switch (option) {
      case 'betting_sports':
        return `betting?page=sport`;
      case 'betting_live':
        return `betting?page=live`;
      case 'betting_result':
        return `betting?page=results`;
    }

    return `${basePath}/${option}`;
  };

  const linkMobileBanners = (option: Omit<BannerData, 'counter'>): string => {
    if (option.disabled) return '#';

    if (option.param === 'favorite') {
      option.param = `games/favorite`;
    }

    let url = `/${locale}/${option.param}`;

    if (option.param.startsWith('https')) {
      url = option.param;
    }

    return url;
  };

  const processBanners = useCallback(
    (order: string[]): BannerData[] => {
      const result: BannerData[] = [];

      for (const id of order) {
        const banner = BANNERS_DATA.find((b) => b.id === id);
        if (!banner) continue;

        const processedBanner = { ...banner };

        if(processedBanner.id === 25 &&!isMillion){
          continue
        }

        if (processedBanner.id === 9) {
          processedBanner.image =
            user.isAuth && loyaltyList?.currentLevel
              ? `${CROWN_IMAGE_URL}${+loyaltyList?.currentLevel - 1}.svg`
              : processedBanner.image;
        }

        // Счетчики
        if (processedBanner.id === 10 && dataCashback) {
          processedBanner.counter = dataCashback.sum > 0 ? 1 : 0;
        }
        if (processedBanner.id === 11 && dataBonuses) {
          processedBanner.counter = dataBonuses.bonuses?.length || 0;
        }
        if (processedBanner.id === 24) {
          processedBanner.counter = wheelsCount;
        }

        result.push(processedBanner);
      }

      return result;
    },
    [dataCashback, dataBonuses, user.isAuth, loyaltyList, wheelsCount]
  );

  const sortedBanners = useMemo(() => {
    if (!user) return [];

    const deviceType = mobileLandscape ? 'mobile' : 'desktop';
    const authKey = user.isAuth ? 'authenticated' : 'unauthenticated';

    let pageKey: 'main' | 'games' | 'betting' = 'main';
    if (pathname.includes('/games')) pageKey = 'games';
    if (pathname.includes('/betting')) pageKey = 'betting';

    const order = SORT_CONFIG_BANNERS[authKey][deviceType][pageKey] || [];
    return processBanners(order);
  }, [user, mobileLandscape, pathname, processBanners]);

  const sortedMobileBanners = useMemo(() => {
    return processBanners(SORT_CONFIG_BANNERS.mobileMenuModal);
  }, [processBanners]);

  const sortedMobileMenuBanners = useMemo(() => {
    const result: BannerData[] = [];
    for (const id of SORT_CONFIG_BANNERS.mobileMenu) {
      const banner = BANNERS_DATA.find((b) => b.id === id);
      if (banner) result.push({ ...banner });
    }
    return result;
  }, []);

  return {
    categoryParam,
    sortedBanners,
    sortedMobileBanners,
    sortedMobileMenuBanners,
    linkBanners,
    linkMobileBanners,
    loading,
  };
};

export { useBanners };
